import ErrorMessage from "@/v2-ui/ErrorMessage"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import { errorToast, toast } from "@/v2-ui/toast"
import { useLayerUpdateMutation } from "@/v2-backoffice-client/layer/update/layer.update.mutation"
import { useLayerSublayerQuery } from "@/v2-backoffice-client/layer/sublayer/layer.sublayer.query"
import { LAYER_SUBLAYER_EDIT_ENVIRONMENT } from "@/v2-backoffice-client/layer/sublayer/layer.sublayer.constants"
import {
  LAYER_FRESHNESS_FREQUENCY_OPTIONS,
  LAYER_FRESHNESS_RESPONSIBILITY_OPTIONS,
} from "@/v2-backoffice-client/layer/view/layer.freshness.constants"

export type LayerFreshnessFieldsProps = {
  layerFeatureCode: string
}

/**
 * Declared update cadence (FUS-1192): frequency and responsibility dropdowns
 * writing to the authoring catalog via updateLayer's mirror. Current values
 * come from the layer's authoring row; on a layer without one, the first save
 * creates it (syncLayerToAuthoring's create-if-missing).
 *
 * Saves on change - the proc is ISNULL-preserving, so a value can be changed
 * but not cleared here; an unset layer just keeps its placeholder.
 */
function LayerFreshnessFields(props: LayerFreshnessFieldsProps) {
  const { layerFeatureCode } = props

  const { data, loading, error, refetch } = useLayerSublayerQuery({
    variables: {
      input: {
        FeatureCode: layerFeatureCode,
        Environment: LAYER_SUBLAYER_EDIT_ENVIRONMENT,
      },
    },
    // A layer without an authoring row answers with a GraphQL error; the
    // selects still work, the first save creates the row. "all" keeps that
    // error readable so a failed request (network, rate limit) can be told
    // apart from "no row yet" instead of both looking like "Not set".
    errorPolicy: "all",
  })
  const studioLayer = data?.getStudioLayer
  const isUnreachable = Boolean(error?.networkError)
  const isLoading = loading && !data

  const [updateLayer, updateState] = useLayerUpdateMutation()
  errorToast(updateState.error, { title: "Failed to update the cadence" })

  // While the values are on their way, or could not be read at all, the
  // selects must not pose as "Not set": that is a real value here.
  const placeholder = isLoading
    ? "Loading…"
    : isUnreachable
      ? "Could not load"
      : "Not set"
  const isDisabled = isLoading || isUnreachable

  async function onSave(field: string, value: string) {
    const result = await updateLayer({
      variables: {
        input: { FeatureCode: layerFeatureCode, [field]: value },
      },
    })
    if (result.data) {
      toast({ title: "Cadence saved." })
      await refetch()
    }
  }

  return (
    <>
      <div className="flex w-44 flex-col gap-1.5">
        <Label htmlFor="layer-details-update-frequency">Update frequency</Label>
        <Select
          value={studioLayer?.UpdateFrequencyCode ?? undefined}
          disabled={isDisabled}
          onValueChange={(value) => void onSave("UpdateFrequencyCode", value)}
        >
          <Select.Trigger
            id="layer-details-update-frequency"
            size="sm"
            aria-busy={isLoading}
            data-feature-code="UI_BackofficeLayers_Details_UpdateFrequency"
          >
            <Select.Value placeholder={placeholder} />
          </Select.Trigger>
          <Select.Content>
            {LAYER_FRESHNESS_FREQUENCY_OPTIONS.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>

      <div className="flex w-44 flex-col gap-1.5">
        <Label htmlFor="layer-details-update-responsibility">
          Update responsibility
        </Label>
        <Select
          value={studioLayer?.UpdateResponsibilityCode ?? undefined}
          disabled={isDisabled}
          onValueChange={(value) =>
            void onSave("UpdateResponsibilityCode", value)
          }
        >
          <Select.Trigger
            id="layer-details-update-responsibility"
            size="sm"
            aria-busy={isLoading}
            data-feature-code="UI_BackofficeLayers_Details_UpdateResponsibility"
          >
            <Select.Value placeholder={placeholder} />
          </Select.Trigger>
          <Select.Content>
            {LAYER_FRESHNESS_RESPONSIBILITY_OPTIONS.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>

      {isUnreachable && (
        <ErrorMessage
          className="basis-full px-0 text-xs"
          error={`The declared cadence could not be loaded: ${error?.message ?? "request failed"}`}
        />
      )}
    </>
  )
}

export default LayerFreshnessFields
